import React from "react";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaEye,
  FaPaperPlane,
  FaVoteYea,
} from "react-icons/fa";

import {
  formatVoteDate,
  formatVoteDateTime,
  getVoteStatus,
  getVoteTypeMeta,
} from "../utils/userVoteUtils";

export default function UserVoteCard({
  vote,
  ballot,
  onParticipate,
  onViewAnswers,
}) {
  const status = getVoteStatus(vote);
  const type = getVoteTypeMeta(vote?.type);
  const answered = Boolean(ballot);
  const canParticipate = status.canParticipate && !answered;

  return (
    <article
      className={`relative overflow-hidden rounded-[26px] border bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:bg-slate-950 ${
        answered
          ? "border-emerald-200 dark:border-emerald-800/60"
          : "border-slate-200 dark:border-slate-800"
      }`}
    >
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      {answered && (
        <div className="absolute right-3 top-3 z-10 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-bold text-white shadow-sm">
          <FaCheckCircle />
          Répondu
        </div>
      )}

      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-blue-50 text-[#013E8E] ring-1 ring-blue-100 dark:bg-blue-900/20 dark:text-blue-200 dark:ring-blue-800/60">
            <FaVoteYea className="text-xl" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap gap-2 pr-20">
              <span
                className={`rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${type.className}`}
              >
                {type.label}
              </span>

              <span
                className={`rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${status.className}`}
              >
                {status.label}
              </span>
            </div>

            <h3 className="mt-3 line-clamp-2 text-lg font-bold text-slate-900 dark:text-white">
              {vote?.title || "Consultation"}
            </h3>

            {vote?.description && (
              <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {vote.description}
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
          <div className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300">
            <FaCalendarAlt className="mt-0.5 shrink-0 text-slate-400" />

            <div className="min-w-0">
              <p>
                <span className="font-bold">Début :</span>{" "}
                {formatVoteDateTime(vote?.start_time)}
              </p>
              <p className="mt-1">
                <span className="font-bold">Fin :</span>{" "}
                {formatVoteDateTime(vote?.end_time)}
              </p>

              {answered && (
                <p className="mt-2 rounded-xl bg-white px-3 py-2 font-bold text-emerald-700 ring-1 ring-emerald-100 dark:bg-slate-950 dark:text-emerald-200 dark:ring-emerald-800/60">
                  Réponse envoyée le {formatVoteDate(ballot?.timestamp)}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {answered ? (
            <button
              type="button"
              onClick={onViewAnswers}
              className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700 ring-1 ring-emerald-200 transition hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60"
            >
              <FaEye />
              Voir mes réponses
            </button>
          ) : (
            <button
              type="button"
              onClick={onParticipate}
              disabled={!canParticipate}
              className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-white/80 dark:disabled:bg-slate-800"
            >
              <FaPaperPlane />
              {canParticipate ? "Répondre" : "Non disponible"}
            </button>
          )}

          <div className="inline-flex min-h-[46px] items-center justify-center rounded-2xl bg-slate-50 px-4 py-3 text-center text-xs font-bold text-slate-500 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:ring-slate-800">
            {answered
              ? "Votre participation est enregistrée"
              : canParticipate
              ? "Participation ouverte"
              : "Participation fermée"}
          </div>
        </div>
      </div>
    </article>
  );
}